/**
 * DashboardViewDetailsContent Component
 * Read-only view of policy rules for Dashboard view
 */

import React from 'react';
import { Text } from '@fluentui/react-components';
import { RuleRow } from './RuleRow';
import { useToggleSet } from '../../../hooks/useToggleSet';
import {
  DashboardPolicy,
  extractSitNames,
  extractLabelNames,
  formatDescription,
  RulePriority,
} from '../types';

type DashboardViewDetailsContentProps = {
  policy: DashboardPolicy;
};

const CONTENT_STYLES = {
  description: {
    display: 'block',
    marginBottom: 16,
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.5,
  },
  rulesContainer: {
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    overflow: 'hidden',
  },
};

export const DashboardViewDetailsContent: React.FC<DashboardViewDetailsContentProps> = ({ 
  policy 
}) => {
  const [expandedRules, toggleRule] = useToggleSet();
  
  return (
    <>
      <Text style={CONTENT_STYLES.description as React.CSSProperties}>
        This policy contains {policy.rules.length} rule{policy.rules.length !== 1 ? 's' : ''}. 
        Expand each rule to see the Sensitive Information Types (SITs) and Labels. 
        SITs and Labels will be automatically created in the tenant if they don't already exist during deployment.
      </Text>

      <div style={CONTENT_STYLES.rulesContainer}>
        {policy.rules.map((rule) => {
          const ruleForDisplay = {
            id: rule.ruleId,
            name: rule.ruleName,
            description: formatDescription(rule.description),
            priority: rule.priority.toString() as RulePriority,
            status: rule.status,
            sitNames: extractSitNames(rule.sits),
            labelNames: extractLabelNames(rule.labels),
          };
          
          return (
            <RuleRow
              key={rule.ruleId}
              rule={ruleForDisplay}
              isExpanded={expandedRules.has(rule.ruleId)}
              onToggle={() => toggleRule(rule.ruleId)}
            />
          );
        })}
      </div>
    </>
  );
};

export default DashboardViewDetailsContent;
